import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { displayLocation } from '../location';
import { STAT_INFO, InfoButton, InfoStrip, ToolbarHelp, FloorPlanGuide } from './FloorPlanHelp';
import { useIsPhone } from '../useIsPhone';

// Warehouse → Floor plan (docs/WAREHOUSE-MODEL.md, step 2). The hall to scale, from
// Fernando's measurements and pallet counts: every metal bay and every pallet as its own
// piece with its permanent id, where it stands today. Click a piece for its sections and
// stock; in "Plan moves" mode a pallet can be dragged (and turned) to where it SHOULD stand —
// that is a plan, nothing moves in the shops. Beside it: the stock that stands on no pallet
// or bay. x = from the left wall, y = from the top wall, cm. Claude, 2026-09-28.

interface Code { code: string; face: string; pieces: number; label?: string | null; box?: string | null; top?: number | null; len?: number | null }
const codeText = (c: Code) => c.label ? `${c.label} (${displayLocation(c.code)})` : displayLocation(c.code);
interface Unit {
  id: string; kind: 'pallet' | 'bay'; row: string; face: string | null; system: string | null; setup: string | null;
  turn: string | null; note: string | null; x: number; y: number; w: number; l: number; rot: number;
  plan_x: number | null; plan_y: number | null; plan_rot: number | null; plan_by: string | null;
  codes: Code[]; pieces: number; top_brand: string | null;
}
interface Walk { metres: number; straight: number; points: [number, number][] }
interface Spot { code: string; face: string; kind: 'reserved' | 'floor'; x: number; w: number; y: number; l: number; pieces: number; note: string | null }
interface Floor {
  hall: { w: number; d: number }; rows: { name: string; kind: string; x: number | null; depth: number | null }[];
  units: Unit[]; spots: Spot[]; zones: { id?: number; kind: string; name: string; x_cm: number | null; y_cm: number | null; w_cm: number | null; h_cm: number | null; entry_x?: number | null; entry_y?: number | null; note: string | null; updated_by?: string | null; blocks_walk?: boolean }[];
  orphans: { code: string; pieces: number; articles: number; top_brand: string | null; why: string }[];
  faces: Record<string, { x: number; w: number; label_x?: number }>;
  stats?: Stats;
  fill?: FillRow[];
  specials?: Special[];
}
/** a special place (Ä, Ö, Å 1–3): drawn when placed; `pick` = a stop of the walk, else taken at the packing table (1.11) */
interface Special {
  id: number; code: string; label: string | null; note: string | null; pick: boolean; zone_id: number | null; source: string | null;
  /** a product's stock location may be set here (CAMPAIGN, 3.11) — Ä / Ö / Å: no */
  home?: boolean;
  x_cm: number | null; y_cm: number | null; w_cm: number | null; h_cm: number | null; pieces: number;
}
interface FillRow {
  row: string; x: number; w: number; top: number; line: number; default: number; min_line: number;
  moved: boolean; by: string | null; per_side: number; pallet_len: number; pallets: number;
  ghosts: { x: number; y: number; w: number; l: number }[];
}
interface Stats {
  pallets: number; pallets_2: number; pallets_3: number; pallets_empty: number;
  bays: number; bays_hi280: number; bays_r3000: number; legs: Record<string, number>;
  bays_walked: number; shelves: number; beams: number; crosses: number;
  sections: number; sections_with_stock: number; reserved: number; floor_spaces: number;
  pieces: number; orphan_codes: number; orphan_pieces: number; planned_moves: number;
  fill_pallets?: number; fill_sections?: number; fill_rows?: { row: string; pallets: number }[];
}

const n0 = (v: number) => v.toLocaleString('en-US').replace(/,/g, ' ');

/** The numbers above the plan — what is in use, from the same data the drawing uses. */
function StatsBar({ s }: { s: Stats }) {
  const legsTotal = Object.values(s.legs).reduce((a, b) => a + b, 0);
  const walked = s.bays_walked > 0;
  const [open, setOpen] = useState<string | null>(null);
  const tile = (label: string, value: React.ReactNode, sub?: React.ReactNode) => (
    <div className="px-3 py-2 border-r border-gray-800 last:border-r-0 min-w-[110px]">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center">{label}
        {STAT_INFO[label] && <InfoButton open={open === label}
          onToggle={() => setOpen(o => (o === label ? null : label))} />}</div>
      <div className="text-lg font-semibold text-white leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
  return (<>
    {open && <InfoStrip label={open} onClose={() => setOpen(null)} />}
    <div className="flex flex-nowrap md:flex-wrap overflow-x-auto md:overflow-visible bg-[#1a1d27] border-b border-gray-800">
      {tile('Pallets', n0(s.pallets), `${s.pallets_3} × 3 per side · ${s.pallets_2} × 2 per side${s.pallets_empty ? ` · ${s.pallets_empty} empty` : ''}`)}
      {tile('Metal bays', n0(s.bays), `${s.bays_hi280} HI280 · ${s.bays_r3000} R3000`)}
      {tile('Legs', n0(legsTotal), Object.entries(s.legs).filter(([, v]) => v).map(([k, v]) => `${v} ${k}`).join(' · '))}
      {tile('Shelves', walked ? n0(s.shelves) : '—', walked ? `${s.bays_walked} of ${s.bays} bays walked` : 'from the units walk — none walked yet')}
      {tile('Back beams / crosses', walked ? `${n0(s.beams)} / ${n0(s.crosses)}` : '—', walked ? 'HI280 beams · R3000 crosses' : 'from the units walk')}
      {tile('Stock sections', n0(s.sections), `${n0(s.sections_with_stock)} with stock · ${s.reserved} reserved · ${s.floor_spaces} floor`)}
      {tile('Pieces placed', n0(s.pieces), s.orphan_codes ? <span className="text-amber-300">{n0(s.orphan_pieces)} pcs on {s.orphan_codes} codes with no place</span> : 'all placed')}
      {s.fill_pallets !== undefined && tile('Max fill',
        <span className="text-emerald-300">+{s.fill_pallets} pallets</span>,
        `${n0(s.pallets + s.fill_pallets)} in all · +${s.fill_sections} sections${s.fill_rows?.length ? ' · ' + s.fill_rows.map(r => `${r.row} +${r.pallets}`).join(', ') : ''}`)}
      {s.planned_moves > 0 && tile('Planned moves', s.planned_moves)}
    </div>
  </>);
}

const role = () => localStorage.getItem('wms_role') || 'staff';
const SNAP = 5;
const snap = (v: number) => Math.round(v / SNAP) * SNAP;

const BOX_COLOR: Record<string, string> = { S: '#3b82f6', M: '#a855f7', SL: '#f97316', LL: '#ef4444', ML: '#14b8a6' };
const BOXES = ['S', 'M', 'SL', 'LL', 'ML'];
// the drawing's frame: the hall plus a margin for the wall labels (cm)
const VB = { x: -80, y: -120, w: 3030, h: 4920 };

type Tool = { kind: 'none' } | { kind: 'measure'; a: [number, number] | null }
  | { kind: 'obstacle' } | { kind: 'station'; id: number } | { kind: 'entrance'; id: number }
  | { kind: 'add' }
  // a special place: a new one to put down ("+ Add a shelf"), or an existing one to move (1.11)
  | { kind: 'special_new' } | { kind: 'special_place'; id: number };

/** "Add a pallet" (2026-10-02): what the server would create in a row — backend _plan_pallet, computed for
 *  real in a rolled-back transaction, so the picking effect is the true one. */
interface AddPreview {
  row: string; place: { x: number; y: number; w: number; l: number } | null; per_side?: number; turn?: string;
  faces: { letter: string; codes: string[]; hole_codes: string[] }[];
  hole: { codes: string[]; from_top_cm: number; length_cm: number } | null;
  defaults: Record<string, string | null>; errors: string[]; warnings: string[];
  picking: { aisle: string; result: 'adds' | 'present' | 'manual' | 'off'; text: string; codes?: string[] }[];
}
interface AddResult { unit_id: string; row: string; codes: string[]; hole: AddPreview['hole']; warnings: string[]; picking: AddPreview['picking'] }

export default function FloorPlan() {
  const navigate = useNavigate();          // → the location page (storage tiers, 2026-10-31)
  const [f, setF] = useState<Floor | null>(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [selZone, setSelZone] = useState<number | null>(null);
  const [edit, setEdit] = useState(false);
  const [scale, setScale] = useState<number | null>(null);          // screen px per cm
  const [drag, setDrag] = useState<{ id: string; dx: number; dy: number; x: number; y: number } | null>(null);
  const [zoneDrag, setZoneDrag] = useState<{ id: number; dx: number; dy: number; x: number; y: number } | null>(null);
  // a special place dragged in planning mode (Fernando 1.11: "I would like to drag and drop them")
  const [specialDrag, setSpecialDrag] = useState<{ id: number; dx: number; dy: number; x: number; y: number } | null>(null);
  const [pan, setPan] = useState<{ sx: number; sy: number; l: number; t: number } | null>(null);
  const [draw, setDraw] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [find, setFind] = useState('');
  const [guide, setGuide] = useState(false);
  const [colour, setColour] = useState<'rows' | 'stacks'>(() => { try { return (localStorage.getItem('wms_floor_colour') as any) || 'rows'; } catch { return 'rows'; } });
  const [tool, setTool] = useState<Tool>({ kind: 'none' });
  const [walkRes, setWalkRes] = useState<{ a: [number, number]; b: [number, number]; w: Walk | null; busy?: boolean } | null>(null);
  const [helpOpen, setHelpOpen] = useState(() => { try { return localStorage.getItem('wms_floor_help') !== '0'; } catch { return true; } });
  const toggleHelp = () => setHelpOpen(o => { try { localStorage.setItem('wms_floor_help', o ? '0' : '1'); } catch { /* private mode */ } return !o; });
  const [lineDrag, setLineDrag] = useState<{ row: string; y: number; min: number; max: number } | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const isPhone = useIsPhone();
  const [sheet, setSheet] = useState(false);          // phone: the side panel as a bottom sheet
  const [addPrev, setAddPrev] = useState<AddPreview | null>(null);
  const [addTypes, setAddTypes] = useState<Record<string, string | null>>({});
  const [addBusy, setAddBusy] = useState(false);
  const [addDone, setAddDone] = useState<AddResult | null>(null);
  // special places (1.11): the selected one, and the form of a new one while "+ Add a shelf" is on
  const [selSpecial, setSelSpecial] = useState<number | null>(null);
  const [newSpecial, setNewSpecial] = useState<{ code: string; note: string; pick: boolean; home: boolean; zone_id: number | '' }>({ code: '', note: '', pick: true, home: false, zone_id: '' });

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/warehouse/floor`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setF(d);
  }, []);
  useEffect(() => { load(); }, [load]);

  // the plan is drawn at its full size and the page scrolls over it (Fernando, 2026-09-28:
  // "I can scroll up and down everywhere") — fit the width at first
  const fitWidth = useCallback(() => {
    const el = box.current;
    if (el) setScale(Math.max(0.05, (el.clientWidth - 24) / VB.w));
  }, []);
  useEffect(() => { if (f && scale === null) fitWidth(); }, [f, scale, fitWidth]);

  const toSvg = (e: { clientX: number; clientY: number }) => {
    const s = svg.current; if (!s) return { x: 0, y: 0 };
    const p = s.createSVGPoint(); p.x = e.clientX; p.y = e.clientY;
    const m = s.getScreenCTM(); if (!m) return { x: 0, y: 0 };
    const q = p.matrixTransform(m.inverse()); return { x: q.x, y: q.y };
  };

  // Ctrl + scroll zooms around the cursor; a plain scroll is the page's own (not touched).
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey) || scale === null) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const cx = (el.scrollLeft + mx) / scale, cy = (el.scrollTop + my) / scale;     // cm under the cursor
      const ns = Math.min(3, Math.max(0.05, scale * (e.deltaY > 0 ? 1 / 1.15 : 1.15)));
      setScale(ns);
      requestAnimationFrame(() => { el.scrollLeft = cx * ns - mx; el.scrollTop = cy * ns - my; });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [scale]);

  // phone: two fingers pinch to zoom around their midpoint; one finger scrolls the page
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let start: { d: number; s: number; cx: number; cy: number; mx: number; my: number } | null = null;
    const dist = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 2 || scale === null) return;
      const r = el.getBoundingClientRect();
      const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left, my = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top;
      start = { d: dist(e.touches), s: scale, cx: (el.scrollLeft + mx) / scale, cy: (el.scrollTop + my) / scale, mx, my };
    };
    const onMove = (e: TouchEvent) => {
      if (!start || e.touches.length !== 2) return;
      e.preventDefault();
      const ns = Math.min(3, Math.max(0.05, start.s * dist(e.touches) / start.d));
      setScale(ns);
      const st = start;
      requestAnimationFrame(() => { el.scrollLeft = st.cx * ns - st.mx; el.scrollTop = st.cy * ns - st.my; });
    };
    const onEnd = (e: TouchEvent) => { if (e.touches.length < 2) start = null; };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    return () => { el.removeEventListener('touchstart', onStart); el.removeEventListener('touchmove', onMove); el.removeEventListener('touchend', onEnd); };
  }, [scale]);
  // selecting something on a phone opens the sheet
  useEffect(() => { if (isPhone && (sel || selZone)) setSheet(true); }, [sel, selZone, isPhone]);

  const posOf = (u: Unit) => (drag && drag.id === u.id)
    ? { x: drag.x, y: drag.y, rot: u.plan_rot ?? u.rot }
    : { x: u.plan_x ?? u.x, y: u.plan_y ?? u.y, rot: u.plan_rot ?? u.rot };

  const post = async (url: string, body: any) => {
    const r = await authFetch(`${API}/warehouse/${url}`, { method: 'POST', body: JSON.stringify(body) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.detail || 'Could not save'); } else setErr('');
    await load();
    return r.ok;
  };
  const savePlan = (id: string, x: number, y: number, rot: number) => post('floor/plan', { unit_id: id, x, y, rot });
  const saveLine = (row: string, y: number | null) => post('floor/fill', y === null ? { row, reset: true } : { row, y });
  const resetPlan = (id: string | null) => post('floor/plan/reset', { unit_id: id });
  const setStacks = (codes: string[], box_type: string | null) => post('floor/stacks', { codes, box_type });
  const saveZone = (body: any) => post('floor/zone', body);
  const saveSpecial = (body: any) => post('floor/special', body);

  const selected = f?.units.find(u => u.id === sel) || null;
  const zoneSel = f?.zones.find((z: any) => z.id === selZone) as any || null;
  const specials: Special[] = f?.specials || [];
  const specialSel = specials.find(s => s.id === selSpecial) || null;
  const planned = (f?.units || []).filter(u => u.plan_x !== null);
  const stations = (f?.zones || []).filter((z: any) => z.kind === 'station') as any[];
  const obstacles = (f?.zones || []).filter((z: any) => z.kind === 'obstacle') as any[];
  const findHit = useMemo(() => {
    const q = find.replace(/\s+/g, '').toUpperCase();
    if (!q || !f) return null;
    return f.units.find(u => u.id.toUpperCase() === q || u.codes.some(c => c.code === q)) || null;
  }, [find, f]);
  useEffect(() => {
    if (!findHit || !box.current || scale === null) return;
    setSel(findHit.id);
    const el = box.current;
    el.scrollTo({ left: (findHit.x - VB.x) * scale - el.clientWidth / 2, top: (findHit.y - VB.y) * scale - el.clientHeight / 2, behavior: 'smooth' });
  }, [findHit]); // eslint-disable-line react-hooks/exhaustive-deps

  // the walk is measured by the server (backend/warehouse_walk.py) — the same engine and the
  // same number as the P&P statistics and the aisle pages; the route comes back as its turns
  const measure = useCallback(async (a: [number, number], b: [number, number]) => {
    setWalkRes({ a, b, w: null, busy: true });
    try {
      const r = await authFetch(`${API}/warehouse/distance`, { method: 'POST', body: JSON.stringify({ points: [a, b] }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setWalkRes({ a, b, w: d.metres == null ? null : { metres: d.metres, straight: d.straight, points: d.points }, busy: false });
    } catch (e: any) { setErr(e.message); setWalkRes({ a, b, w: null, busy: false }); }
  }, []);
  // a measurement is redone when what blocks the way changes (a move, an obstacle, the mezzanine switch)
  useEffect(() => {
    if (walkRes && !walkRes.busy) measure(walkRes.a, walkRes.b);
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  const grid = f ? true : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setTool({ kind: 'none' }); setDraw(null); setAddPrev(null); setSelSpecial(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (err && !f) return <div className="p-6 text-red-400">{err}</div>;
  if (!f) return <div className="p-6 text-gray-500">Loading…</div>;
  const mezz = f.zones.find(z => z.kind === 'mezzanine');
  const canEdit = role() !== 'viewer';
  // adding a pallet changes the model and the picking stops — manager/admin, like applying a walk
  const canApply = role() === 'admin' || role() === 'manager';

  const previewAdd = async (row: string) => {
    setAddDone(null); setAddBusy(true); setErr('');
    try {
      const r = await authFetch(`${API}/warehouse/floor/add-pallet?row=${encodeURIComponent(row)}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); setAddPrev(null); return; }
      setAddPrev(d); setAddTypes(d.defaults || {}); setSel(null); setSelZone(null);
      if (isPhone) setSheet(true);
    } finally { setAddBusy(false); }
  };
  const confirmAdd = async () => {
    if (!addPrev || addBusy) return;
    setAddBusy(true); setErr('');
    try {
      const codes = addPrev.faces.flatMap(x => x.codes);
      const r = await authFetch(`${API}/warehouse/floor/add-pallet`, {
        method: 'POST', body: JSON.stringify({ row: addPrev.row, codes, box_types: addTypes }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
      setAddDone(d); setAddPrev(null); setTool({ kind: 'none' });
      await load();
      setSel(d.unit_id);
    } finally { setAddBusy(false); }
  };
  const sc = scale ?? 0.3;

  /** a click point snaps to a station entrance nearby (the usual start of a walk) */
  const snapPoint = (p: { x: number; y: number }): [number, number] => {
    for (const s of stations) {
      if (s.entry_x !== null && Math.hypot(s.entry_x - p.x, s.entry_y - p.y) < 60) return [s.entry_x, s.entry_y];
    }
    return [p.x, p.y];
  };
  const onPlanClick = (p: { x: number; y: number }) => {
    if (tool.kind === 'measure' && grid) {
      const pt = snapPoint(p);
      if (!tool.a) setTool({ kind: 'measure', a: pt });
      else { measure(tool.a, pt); setTool({ kind: 'measure', a: null }); }
      return true;
    }
    if (tool.kind === 'station') {
      const z = stations.find(s => s.id === tool.id);
      const w = z?.w_cm || 250, h = z?.h_cm || 200;
      saveZone({ id: tool.id, x: snap(p.x - w / 2), y: snap(p.y - h / 2), w, h });
      setTool({ kind: 'none' }); setSelZone(tool.id);
      return true;
    }
    if (tool.kind === 'entrance') {
      saveZone({ id: tool.id, entry_x: snap(p.x), entry_y: snap(p.y) });
      setTool({ kind: 'none' }); setSelZone(tool.id);
      return true;
    }
    if (tool.kind === 'special_place') {
      const s = specials.find(x => x.id === tool.id);
      const w = s?.w_cm || 130, h = s?.h_cm || 40;
      saveSpecial({ id: tool.id, x: snap(p.x - w / 2), y: snap(p.y - h / 2), w, h });
      setTool({ kind: 'none' }); setSelSpecial(tool.id); setSel(null); setSelZone(null);
      return true;
    }
    if (tool.kind === 'special_new') {
      if (!newSpecial.code.trim()) { setErr('Give the shelf a code first (Å 4)'); return true; }
      (async () => {
        const ok = await saveSpecial({ code: newSpecial.code, note: newSpecial.note, pick: newSpecial.pick, home: newSpecial.home,
                                       zone_id: newSpecial.zone_id === '' ? null : newSpecial.zone_id,
                                       x: snap(p.x - 65), y: snap(p.y - 20), w: 130, h: 40 });
        if (ok) { setTool({ kind: 'none' }); setNewSpecial({ code: '', note: '', pick: true, home: false, zone_id: '' }); }
      })();
      return true;
    }
    return false;
  };

  // face letters sit above a row's end — for a pallet row above its fill line, clear of its "+N"
  const faceLabels = Object.entries(f.faces).map(([L, b]) => {
    const ys = f.units.filter(u => u.codes.some(c => c.face === L)).map(u => u.y);
    let top = ys.length ? Math.min(...ys) : 300;
    const fr = (f.fill || []).find(r => r.row.split('|').includes(L));
    if (fr) top = Math.min(top, lineDrag && lineDrag.row === fr.row ? lineDrag.y : fr.line) - 40;
    return { L, x: b.label_x ?? b.x + b.w / 2, y: top - 25 };
  });
  const toolHint = tool.kind === 'measure' ? (tool.a ? 'Click the end point (a pallet, a bay, a station entrance or any spot)' : 'Click the start point — near a station entrance it snaps to it')
    : tool.kind === 'obstacle' ? 'Drag a rectangle over what blocks the way'
    : tool.kind === 'station' ? `Click where ${stations.find(s => s.id === tool.id)?.name} stands`
    : tool.kind === 'entrance' ? `Click the entrance of ${stations.find(s => s.id === tool.id)?.name}`
    : tool.kind === 'add' ? 'Click a highlighted place at the top of a row'
    : tool.kind === 'special_place' ? `Click where ${displayLocation(specials.find(s => s.id === tool.id)?.code)} stands`
    : tool.kind === 'special_new' ? 'Fill in the shelf on the right, then click where it stands' : '';

  const nextInRow = (u: Unit) => f.units.filter(x => x.row === u.row && x.kind === u.kind && x.y < u.y)
    .sort((a, b) => b.y - a.y)[0] || null;

  return (
    <div className="flex h-full">
      {guide && <FloorPlanGuide onClose={() => setGuide(false)} />}
      <div ref={box} className="flex-1 min-w-0 overflow-auto bg-[#0f1117]">
        {f.stats && <StatsBar s={f.stats} />}
        <div className="sticky top-0 z-20 flex gap-2 items-center flex-wrap px-3 py-2 bg-[#0f1117]/95 border-b border-gray-800">
          <input value={find} onChange={e => setFind(e.target.value)} placeholder="Find a code or unit (M 23, P-0012)"
            className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white w-60" />
          <button onClick={() => { fitWidth(); box.current?.scrollTo({ top: 0, left: 0 }); }} className="px-3 py-1.5 rounded bg-gray-800 text-gray-200 text-sm">Whole hall</button>
          <select value={colour} onChange={e => { const v = e.target.value as any; setColour(v); try { localStorage.setItem('wms_floor_colour', v); } catch { /* */ } }}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-gray-200">
            <option value="rows">Colour: pallet direction</option>
            <option value="stacks">Colour: box stacks</option>
          </select>
          <button onClick={() => { setTool(t => t.kind === 'measure' ? { kind: 'none' } : { kind: 'measure', a: null }); }}
            className={`px-3 py-1.5 rounded text-sm ${tool.kind === 'measure' ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-200'}`}>Measure</button>
          {canEdit && <button onClick={() => { setEdit(e => !e); setTool({ kind: 'none' }); }}
            className={`px-3 py-1.5 rounded text-sm ${edit ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-200'}`}>
            {edit ? 'Planning — drag pallets, lines, stations' : 'Plan moves'}</button>}
          {edit && <button onClick={() => setTool(t => t.kind === 'obstacle' ? { kind: 'none' } : { kind: 'obstacle' })}
            className={`px-3 py-1.5 rounded text-sm ${tool.kind === 'obstacle' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-200'}`}>+ Obstacle</button>}
          {canApply && <button onClick={() => { setTool(t => t.kind === 'add' ? { kind: 'none' } : { kind: 'add' }); setAddPrev(null); setAddDone(null); }}
            className={`px-3 py-1.5 rounded text-sm ${tool.kind === 'add' ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-200'}`}>+ Add a pallet</button>}
          {canApply && <button onClick={() => { setTool(t => t.kind === 'special_new' ? { kind: 'none' } : { kind: 'special_new' }); setAddPrev(null); setAddDone(null); setSel(null); setSelZone(null); setSelSpecial(null); if (isPhone) setSheet(true); }}
            className={`px-3 py-1.5 rounded text-sm ${tool.kind === 'special_new' ? 'bg-amber-600 text-white' : 'bg-gray-800 text-gray-200'}`}>+ Add a shelf</button>}
          {canEdit && <a href="/warehouse/stacks" className="px-3 py-1.5 rounded bg-gray-800 text-gray-200 text-sm">Mark stacks</a>}
          <button onClick={() => setGuide(true)} className="px-3 py-1.5 rounded bg-gray-800 text-gray-200 text-sm">Guide</button>

          {planned.length > 0 && <span className="text-xs text-amber-300">{planned.length} planned move{planned.length === 1 ? '' : 's'}</span>}
          {toolHint && <span className="text-xs text-emerald-300">{toolHint} · Esc cancels</span>}
          {walkRes && (
            <span className="text-sm text-white bg-emerald-900/60 border border-emerald-700 rounded px-2 py-1">
              {walkRes.busy ? 'Measuring…' : walkRes.w ? <>Walk <b>{walkRes.w.metres.toFixed(1)} m</b> · straight {walkRes.w.straight.toFixed(1)} m</> : 'No way through'}
              <button onClick={() => setWalkRes(null)} className="ml-2 text-gray-300">✕</button>
            </span>
          )}
          {err && <span className="text-xs text-red-400">{err}</span>}
        </div>
        <svg ref={svg} className="select-none block" width={VB.w * sc} height={VB.h * sc}
          viewBox={`${VB.x} ${VB.y} ${VB.w} ${VB.h}`}
          style={{ cursor: tool.kind !== 'none' ? 'crosshair' : pan ? 'grabbing' : 'default' }}
          onPointerDown={e => {
            const bg = e.target === svg.current || (e.target as Element).getAttribute('data-bg');
            if (!bg) return;
            const p = toSvg(e);
            if (tool.kind === 'obstacle') { setDraw({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); return; }
            if (tool.kind !== 'none') return;                       // tools act on the click
            if (e.pointerType !== 'touch' && box.current) setPan({ sx: e.clientX, sy: e.clientY, l: box.current.scrollLeft, t: box.current.scrollTop });
          }}
          onClick={e => {
            const bg = e.target === svg.current || (e.target as Element).getAttribute('data-bg');
            if (bg && tool.kind !== 'none' && tool.kind !== 'obstacle') onPlanClick(toSvg(e));
          }}
          onPointerMove={e => {
            if (draw) { const p = toSvg(e); setDraw(d => d && { ...d, x1: p.x, y1: p.y }); }
            else if (lineDrag) { const p = toSvg(e); setLineDrag(d => d && { ...d, y: Math.min(d.max, Math.max(d.min, snap(p.y))) }); }
            else if (drag) { const p = toSvg(e); setDrag(d => d && { ...d, x: snap(p.x - d.dx), y: snap(p.y - d.dy) }); }
            else if (zoneDrag) { const p = toSvg(e); setZoneDrag(d => d && { ...d, x: snap(p.x - d.dx), y: snap(p.y - d.dy) }); }
            else if (specialDrag) { const p = toSvg(e); setSpecialDrag(d => d && { ...d, x: snap(p.x - d.dx), y: snap(p.y - d.dy) }); }
            else if (pan && box.current) { box.current.scrollLeft = pan.l - (e.clientX - pan.sx); box.current.scrollTop = pan.t - (e.clientY - pan.sy); }
          }}
          onPointerUp={() => {
            if (draw) {
              const x = Math.min(draw.x0, draw.x1), y = Math.min(draw.y0, draw.y1);
              const w = Math.abs(draw.x1 - draw.x0), l = Math.abs(draw.y1 - draw.y0);
              if (w >= 20 && l >= 20) saveZone({ kind: 'obstacle', x: snap(x), y: snap(y), w: snap(w), h: snap(l) });
              setDraw(null); setTool({ kind: 'none' });
            }
            if (lineDrag) {
              const fr = f.fill?.find(r => r.row === lineDrag.row);
              if (fr && Math.abs(fr.line - lineDrag.y) > 1) saveLine(lineDrag.row, lineDrag.y);
              setLineDrag(null);
            }
            if (drag) {
              const u = f.units.find(x => x.id === drag.id);
              const moved = u && (Math.abs(drag.x - (u.plan_x ?? u.x)) > 1 || Math.abs(drag.y - (u.plan_y ?? u.y)) > 1);
              if (u && moved) savePlan(u.id, drag.x, drag.y, u.plan_rot ?? u.rot);
              setDrag(null);
            }
            if (zoneDrag) {
              const z = f.zones.find((q: any) => q.id === zoneDrag.id) as any;
              if (z && (Math.abs(zoneDrag.x - z.x_cm) > 1 || Math.abs(zoneDrag.y - z.y_cm) > 1)) {
                const dx = zoneDrag.x - z.x_cm, dy = zoneDrag.y - z.y_cm;
                saveZone({ id: z.id, x: zoneDrag.x, y: zoneDrag.y,
                  ...(z.entry_x !== null ? { entry_x: z.entry_x + dx, entry_y: z.entry_y + dy } : {}) });
              }
              setZoneDrag(null);
            }
            if (specialDrag) {
              const s = specials.find(q => q.id === specialDrag.id);
              if (s && s.x_cm !== null && (Math.abs(specialDrag.x - s.x_cm) > 1 || Math.abs(specialDrag.y - (s.y_cm || 0)) > 1)) {
                saveSpecial({ id: s.id, x: specialDrag.x, y: specialDrag.y, w: s.w_cm || 130, h: s.h_cm || 40 });
              }
              setSpecialDrag(null);
            }
            setPan(null);
          }}>
          <defs>
            <pattern id="hatch" width="24" height="24" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="24" stroke="#4b5563" strokeWidth="6" />
            </pattern>
          </defs>
          <rect data-bg="1" x={VB.x} y={VB.y} width={VB.w} height={VB.h} fill="#0f1117" />
          <rect data-bg="1" x={0} y={0} width={f.hall.w} height={f.hall.d} fill="#161a23" stroke="#9ca3af" strokeWidth={8} />
          <text x={f.hall.w / 2} y={-70} textAnchor="middle" fontSize={40} fill="#9ca3af" pointerEvents="none">top wall · cargo doors · packing</text>
          <text x={f.hall.w / 2} y={f.hall.d + 60} textAnchor="middle" fontSize={40} fill="#6b7280" pointerEvents="none">bottom wall · offices</text>
          {mezz && mezz.w_cm && mezz.h_cm && (
            <g pointerEvents="none">
              <rect x={mezz.x_cm || 0} y={mezz.y_cm || 0} width={mezz.w_cm} height={mezz.h_cm} fill="url(#hatch)" opacity={0.35} />
              <text x={(mezz.w_cm) / 2} y={60} textAnchor="middle" fontSize={36} fill="#9ca3af">mezzanine (approx.)</text>
            </g>
          )}
          {f.spots.map(s => (
            <g key={s.code}>
              <rect x={s.x} y={s.y} width={s.w} height={s.l} fill={s.kind === 'reserved' ? 'url(#hatch)' : 'none'}
                stroke={s.pieces > 0 ? '#f87171' : '#6b7280'} strokeDasharray="10 8" strokeWidth={s.pieces > 0 ? 6 : 3} />
              <title>{`${displayLocation(s.code)} — ${s.kind === 'reserved' ? 'reserved hole' : 'floor space'}${s.pieces ? `, ${s.pieces} pcs` : ''}`}</title>
            </g>
          ))}
          {/* maximum fill: where a pallet can still go, and each row's draggable fill line */}
          {(f.fill || []).map(fr => {
            const y = lineDrag && lineDrag.row === fr.row ? lineDrag.y : fr.line;
            const color = fr.per_side === 2 ? '#7c5a1e' : '#1f6f63';
            const shown = fr.ghosts.filter(g => g.y >= y - 0.5);
            const next = shown.length ? shown.reduce((a, b) => (b.y > a.y ? b : a)) : null;   // right above the row's top pallet
            const adding = tool.kind === 'add';
            return (
              <g key={`fill${fr.row}`}>
                {shown.map((g, i) => {
                  const isNext = adding && g === next;
                  const picked = isNext && addPrev?.row === fr.row;
                  return (
                    <rect key={i} x={g.x} y={g.y} width={g.w} height={g.l} fill={isNext ? '#059669' : color}
                      opacity={adding ? (isNext ? 0.75 : 0.12) : 0.3}
                      stroke={picked ? '#60a5fa' : '#34d399'} strokeDasharray={isNext ? undefined : '10 6'} strokeWidth={picked ? 10 : isNext ? 6 : 3}
                      style={{ cursor: isNext ? 'pointer' : 'default' }}
                      onClick={isNext ? e => { e.stopPropagation(); previewAdd(fr.row); } : undefined}>
                      <title>{isNext ? `${fr.row}: add a pallet here` : `${fr.row}: a pallet can go here`}</title>
                    </rect>
                  );
                })}
                <g style={{ cursor: edit ? 'ns-resize' : 'default', touchAction: edit ? 'none' : 'auto' }}
                  onPointerDown={e => {
                    if (!edit || tool.kind !== 'none') return;
                    e.stopPropagation();
                    setLineDrag({ row: fr.row, y: fr.line, min: fr.min_line, max: fr.top });
                    (e.target as Element).setPointerCapture?.(e.pointerId);
                  }}>
                  <line x1={fr.x - 20} x2={fr.x + fr.w + 20} y1={y} y2={y} stroke="#34d399" strokeWidth={edit ? 10 : 5} strokeDasharray={edit ? undefined : '16 10'} />
                  <rect x={fr.x - 20} y={y - 18} width={fr.w + 40} height={36} fill="transparent" />
                  <text x={fr.x + fr.w / 2} y={y - 12} textAnchor="middle" fontSize={26} fill="#34d399">
                    {`+${lineDrag && lineDrag.row === fr.row
                      ? shown.length + (y < fr.line ? Math.floor((fr.line - y) / fr.pallet_len) : 0)
                      : fr.pallets}`}</text>
                </g>
              </g>
            );
          })}
          {faceLabels.map(fl => <text key={fl.L} x={fl.x} y={fl.y} textAnchor="middle" fontSize={48} fontWeight={700} fill="#e5e7eb" pointerEvents="none">{fl.L}</text>)}
          {/* where a planned unit stands today */}
          {planned.map(u => <rect key={`g${u.id}`} x={u.x} y={u.y} width={u.w} height={u.l} fill="none" stroke="#f59e0b" strokeDasharray="12 8" strokeWidth={4} pointerEvents="none" />)}
          {f.units.map(u => {
            const p = posOf(u);
            const isSel = sel === u.id;
            const isPlan = u.plan_x !== null || (drag && drag.id === u.id);
            const rowFill = u.kind === 'bay' ? '#334155' : u.turn === 'short' ? '#7c5a1e' : '#1f6f63';
            const w = (p.rot % 180 === 0) ? u.w : u.l, l = (p.rot % 180 === 0) ? u.l : u.w;
            const leftFace = u.row.includes('|') ? u.row.split('|')[1] : null;
            const stacked = colour === 'stacks' && u.kind === 'pallet';
            return (
              <g key={u.id} transform={`translate(${p.x},${p.y})`}
                style={{ cursor: tool.kind !== 'none' ? 'crosshair' : edit && u.kind === 'pallet' ? 'grab' : 'pointer',
                         touchAction: edit && u.kind === 'pallet' ? 'none' : 'auto' }}
                onPointerDown={e => {
                  e.stopPropagation();
                  if (tool.kind === 'none' && edit && u.kind === 'pallet') {
                    const q = toSvg(e); setDrag({ id: u.id, dx: q.x - p.x, dy: q.y - p.y, x: p.x, y: p.y });
                    (e.target as Element).setPointerCapture?.(e.pointerId);
                  }
                }}
                onClick={e => {
                  // a TAP — a finger that scrolled the page never gets here (2026-09-28: scrolling
                  // on a phone kept selecting pallets and opening the panel)
                  e.stopPropagation();
                  if (tool.kind === 'measure' || tool.kind === 'station' || tool.kind === 'entrance') {
                    onPlanClick(tool.kind === 'measure' ? { x: p.x + w / 2, y: p.y + l / 2 } : toSvg(e)); return;
                  }
                  setSel(u.id); setSelZone(null);
                }}>
                <rect width={w} height={l} fill={stacked ? '#1f2937' : rowFill} opacity={u.pieces > 0 ? 1 : 0.55}
                  stroke={isSel ? '#60a5fa' : isPlan ? '#f59e0b' : '#0f1117'} strokeWidth={isSel ? 8 : isPlan ? 6 : 2} />
                {stacked && p.rot % 180 === 0 && u.codes.map(c => c.top !== null && (
                  <g key={c.code}>
                    <rect x={c.face === leftFace ? 3 : w / 2 + 1} y={(c.top as number) - u.y + 2} width={w / 2 - 4} height={(c.len || 40) - 4}
                      fill={c.box ? BOX_COLOR[c.box] : '#374151'} opacity={c.box ? 0.9 : 0.5} pointerEvents="none" />
                    {sc > 0.35 && c.box && <text x={c.face === leftFace ? w / 4 : 3 * w / 4} y={(c.top as number) - u.y + (c.len || 40) / 2 + 6}
                      textAnchor="middle" fontSize={16} fill="#fff" pointerEvents="none">{c.box}</text>}
                  </g>
                ))}
                {stacked && p.rot % 180 !== 0 && u.setup && <text x={w / 2} y={l / 2 + 6} textAnchor="middle" fontSize={16} fill="#fff" pointerEvents="none">{u.setup}</text>}
                {!stacked && sc > 0.35 && <text x={w / 2} y={l / 2 + 6} textAnchor="middle" fontSize={Math.min(18, w / 4)} fill="#e5e7eb" pointerEvents="none">
                  {u.codes.length ? (u.codes[0].label || displayLocation(u.codes[0].code)) : u.id}</text>}
                <title>{`${u.id} · ${u.codes.map(codeText).join(', ')} · ${u.pieces} pcs${u.setup ? ` · ${u.setup}` : ''}`}</title>
              </g>
            );
          })}
          {/* obstacles and stations */}
          {obstacles.map(o => (
            <g key={`o${o.id}`} onClick={e => { e.stopPropagation(); setSelZone(o.id); setSel(null); }}
              onPointerDown={e => { e.stopPropagation();
              if (edit && tool.kind === 'none') { const q = toSvg(e); setZoneDrag({ id: o.id, dx: q.x - o.x_cm, dy: q.y - o.y_cm, x: o.x_cm, y: o.y_cm }); (e.target as Element).setPointerCapture?.(e.pointerId); } }}>
              <rect x={zoneDrag && zoneDrag.id === o.id ? zoneDrag.x : o.x_cm} y={zoneDrag && zoneDrag.id === o.id ? zoneDrag.y : o.y_cm} width={o.w_cm} height={o.h_cm}
                fill="#dc2626" opacity={0.45} stroke={selZone === o.id ? '#60a5fa' : '#f87171'} strokeWidth={selZone === o.id ? 8 : 4} />
              <title>{`${o.name}${o.note ? ` — ${o.note}` : ''}`}</title>
            </g>
          ))}
          {stations.filter(s => s.x_cm !== null).map(s => {
            const x = zoneDrag && zoneDrag.id === s.id ? zoneDrag.x : s.x_cm, y = zoneDrag && zoneDrag.id === s.id ? zoneDrag.y : s.y_cm;
            const ex = s.entry_x !== null ? s.entry_x + (x - s.x_cm) : null, ey = s.entry_y !== null ? s.entry_y + (y - s.y_cm) : null;
            return (
              <g key={`s${s.id}`} onClick={e => { e.stopPropagation();
                  if (tool.kind === 'measure') { onPlanClick(toSvg(e)); return; }
                  setSelZone(s.id); setSel(null); }}
                onPointerDown={e => { e.stopPropagation();
                  if (edit && tool.kind === 'none') { const q = toSvg(e); setZoneDrag({ id: s.id, dx: q.x - s.x_cm, dy: q.y - s.y_cm, x: s.x_cm, y: s.y_cm }); (e.target as Element).setPointerCapture?.(e.pointerId); } }}>
                <rect x={x} y={y} width={s.w_cm} height={s.h_cm} fill="#1d4ed8" opacity={0.35}
                  stroke={selZone === s.id ? '#60a5fa' : '#3b82f6'} strokeWidth={selZone === s.id ? 8 : 4} />
                <text x={x + s.w_cm / 2} y={y + s.h_cm / 2 + 8} textAnchor="middle" fontSize={26} fill="#dbeafe" pointerEvents="none">{s.name}</text>
                {ex !== null && <g pointerEvents="none"><circle cx={ex} cy={ey} r={18} fill="#f59e0b" /><text x={ex} y={ey - 26} textAnchor="middle" fontSize={20} fill="#fbbf24">entrance</text></g>}
              </g>
            );
          })}
          {/* special places with a position (Å 1–3 against Inbound station 1, 1.11): small amber shelves */}
          {specials.filter(s => s.x_cm !== null && s.y_cm !== null).map(s => {
            const sx = specialDrag && specialDrag.id === s.id ? specialDrag.x : s.x_cm!;
            const sy = specialDrag && specialDrag.id === s.id ? specialDrag.y : s.y_cm!;
            const sw = s.w_cm || 130, sh = s.h_cm || 40;
            return (
              <g key={`sp${s.id}`} style={{ cursor: edit && tool.kind === 'none' ? 'move' : undefined }}
                onClick={e => { e.stopPropagation();
                  if (tool.kind === 'measure') { onPlanClick(toSvg(e)); return; }
                  setSelSpecial(s.id); setSel(null); setSelZone(null); if (isPhone) setSheet(true); }}
                onPointerDown={e => { e.stopPropagation();
                  // drag in planning mode, like a station (Fernando 1.11); the drop is saved on release
                  if (edit && tool.kind === 'none' && canApply) { const q = toSvg(e); setSpecialDrag({ id: s.id, dx: q.x - s.x_cm!, dy: q.y - s.y_cm!, x: s.x_cm!, y: s.y_cm! }); (e.target as Element).setPointerCapture?.(e.pointerId); } }}>
                <rect x={sx} y={sy} width={sw} height={sh} fill="#b45309" opacity={0.8}
                  stroke={selSpecial === s.id ? '#60a5fa' : '#f59e0b'} strokeWidth={selSpecial === s.id ? 8 : 4} />
                <text x={sx + sw / 2} y={sy + sh / 2 + 8} textAnchor="middle" fontSize={Math.min(24, Math.max(14, Math.min(sw, sh) * 0.55))} fontWeight={700} fill="#fff7ed" pointerEvents="none">{displayLocation(s.code)}</text>
                <title>{`${displayLocation(s.code)}${s.note ? ` — ${s.note}` : ''} · ${sw} × ${sh} cm · ${s.pieces} pcs${s.pick ? ' · picked on the way' : ' · taken at the packing table'}${s.home ? ' · products live here' : ''}`}</title>
              </g>
            );
          })}
          {draw && <rect x={Math.min(draw.x0, draw.x1)} y={Math.min(draw.y0, draw.y1)} width={Math.abs(draw.x1 - draw.x0)} height={Math.abs(draw.y1 - draw.y0)} fill="#dc2626" opacity={0.3} stroke="#f87171" strokeDasharray="10 6" strokeWidth={4} pointerEvents="none" />}
          {/* the measured walk */}
          {tool.kind === 'measure' && tool.a && <circle cx={tool.a[0]} cy={tool.a[1]} r={20} fill="#34d399" pointerEvents="none" />}
          {walkRes && (
            <g pointerEvents="none">
              {walkRes.w && <polyline points={walkRes.w.points.map(q => q.join(',')).join(' ')} fill="none" stroke="#34d399" strokeWidth={10} strokeLinejoin="round" />}
              <circle cx={walkRes.a[0]} cy={walkRes.a[1]} r={20} fill="#34d399" />
              <circle cx={walkRes.b[0]} cy={walkRes.b[1]} r={20} fill="#10b981" stroke="#fff" strokeWidth={4} />
              {walkRes.w && <text x={walkRes.b[0]} y={walkRes.b[1] - 34} textAnchor="middle" fontSize={34} fontWeight={700} fill="#6ee7b7">{walkRes.w.metres.toFixed(1)} m</text>}
            </g>
          )}
        </svg>
        <div className="px-3 py-3 text-xs text-gray-400 border-t border-gray-800 space-y-1">
          <button onClick={toggleHelp} className="text-gray-500 hover:text-white">{helpOpen ? 'Hide help ▾' : 'Show help ▴'}</button>
          {helpOpen && <>
            <ToolbarHelp />
            <div className="space-x-3 pt-1 border-t border-gray-800">
              {colour === 'rows' ? <>
                <span><span className="inline-block w-3 h-3 align-middle mr-1" style={{ background: '#334155' }} />metal bay</span>
                <span><span className="inline-block w-3 h-3 align-middle mr-1" style={{ background: '#1f6f63' }} />pallet, 3 per side</span>
                <span><span className="inline-block w-3 h-3 align-middle mr-1" style={{ background: '#7c5a1e' }} />pallet, 2 per side</span>
              </> : <>
                {BOXES.map(b => <span key={b}><span className="inline-block w-3 h-3 align-middle mr-1" style={{ background: BOX_COLOR[b] }} />{b}</span>)}
                <span><span className="inline-block w-3 h-3 align-middle mr-1" style={{ background: '#374151' }} />not marked yet</span>
              </>}
              <span>dashed = reserved hole / floor space (red: stock on it)</span>
              <span className="text-emerald-300">light = a pallet can go here · green line = fill line</span>
              <span className="text-blue-300">blue = station (orange dot = entrance)</span>
              <span className="text-red-300">red = obstacle</span>
            </div>
          </>}
        </div>
      </div>

      {isPhone && !sheet && (
        <button onClick={() => setSheet(true)}
          className="fixed bottom-4 right-4 z-40 px-4 py-3 rounded-full bg-blue-600 text-white text-sm font-semibold shadow-xl">
          {selected ? `${selected.id} ▴` : zoneSel ? `${zoneSel.name} ▴` : 'Panel ▴'}
        </button>
      )}
      {(!isPhone || sheet) && <aside className={isPhone
        ? 'fixed left-0 right-0 bottom-0 z-40 max-h-[55vh] overflow-auto rounded-t-2xl border-t border-gray-700 bg-[#1a1d27] p-4 text-sm shadow-2xl'
        : 'w-80 border-l border-gray-800 bg-[#1a1d27] overflow-auto p-4 text-sm'}>
        {isPhone && (
          <div className="sticky -top-4 -mx-4 -mt-4 mb-3 px-4 py-2 bg-[#1a1d27] border-b border-gray-800 flex items-center justify-between z-10">
            <span className="text-xs text-gray-400">{selected ? selected.id : zoneSel ? zoneSel.name : 'Details'}</span>
            <button onClick={() => setSheet(false)} className="px-3 py-1.5 rounded-lg bg-gray-700 text-white text-sm">✕ Close</button>
          </div>
        )}
        {addDone && (
          <div className="mb-4 rounded-lg border border-emerald-700 bg-emerald-950/40 p-3 text-xs space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="text-emerald-200 font-semibold text-sm">{addDone.unit_id} added in {addDone.row}</span>
              <button onClick={() => setAddDone(null)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            <div className="text-gray-200">{addDone.codes.map(displayLocation).join(' · ')}</div>
            {addDone.hole && <div className="text-gray-400">Pillar hole reserved: {addDone.hole.codes.map(displayLocation).join(' · ')}</div>}
            {addDone.picking.map(p => <div key={p.aisle} className={p.result === 'adds' || p.result === 'present' ? 'text-emerald-300' : 'text-amber-300'}>{p.text}</div>)}
            {addDone.warnings.map(w => <div key={w} className="text-amber-300">{w}</div>)}
            <a href={`/tools/wh-labels?codes=${encodeURIComponent(addDone.codes.join(','))}`}
              className="inline-block mt-1 px-3 py-1.5 rounded bg-blue-600 text-white">🖨 Print the location labels</a>
          </div>
        )}
        {tool.kind === 'add' ? (
          <div className="mb-6">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-semibold text-white">{addPrev ? `New pallet in ${addPrev.row}` : 'Add a pallet'}</h2>
              <button onClick={() => { setTool({ kind: 'none' }); setAddPrev(null); }} className="text-gray-500 hover:text-white">✕</button>
            </div>
            {!addPrev ? (
              <div className="text-gray-400 text-xs space-y-1 mt-1">
                <div>{addBusy ? 'Working it out…' : 'Click the highlighted place at the top of a row. A pallet always joins its row at the top end; its codes continue the row\'s numbering on both faces.'}</div>
                <div className="text-gray-500">A row without a highlighted place is full up to its fill line — drag the line in "Plan moves" first, if there is room.</div>
              </div>
            ) : (
              <div className="mt-1 space-y-2">
                <div className="text-gray-400 text-xs">{addPrev.per_side} stacks per side, like the row's other pallets{addPrev.place ? ` · ${Math.round(addPrev.place.y)}–${Math.round(addPrev.place.y + addPrev.place.l)} cm from the top wall` : ''}</div>
                {addPrev.errors.length > 0 ? (
                  <div className="rounded border border-red-800 bg-red-950/40 p-2 text-red-200 text-xs space-y-1">
                    {addPrev.errors.map(e => <div key={e}>{e}</div>)}
                  </div>
                ) : <>
                  {addPrev.hole && <div className="text-xs text-gray-300">First the pillar: {addPrev.hole.codes.map(displayLocation).join(' · ')} become a reserved hole ({Math.round(addPrev.hole.length_cm)} cm).</div>}
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Box stacks — the whole pallet:</div>
                    <div className="flex flex-wrap gap-1">
                      {BOXES.map(b => <button key={b} onClick={() => setAddTypes(Object.fromEntries(addPrev.faces.flatMap(x => x.codes).map(c => [c, b])))}
                        className="px-2.5 py-1 rounded text-white text-xs" style={{ background: BOX_COLOR[b] }}>All {b}</button>)}
                    </div>
                  </div>
                  {addPrev.faces.map(fc => (
                    <div key={fc.letter} className="space-y-1">
                      <div className="text-xs text-gray-500">Face {fc.letter}</div>
                      {fc.codes.map(c => (
                        <div key={c} className="flex justify-between items-center gap-2">
                          <span className="text-white font-medium">{displayLocation(c)}</span>
                          <span className="flex gap-0.5">
                            {BOXES.map(b => <button key={b} onClick={() => setAddTypes(t => ({ ...t, [c]: t[c] === b ? null : b }))}
                              className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: addTypes[c] === b ? BOX_COLOR[b] : '#374151', color: '#fff' }}>{b}</button>)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                  <div className="space-y-0.5 text-xs">
                    <div className="text-gray-500">Picking:</div>
                    {addPrev.picking.map(p => <div key={p.aisle} className={p.result === 'adds' || p.result === 'present' ? 'text-emerald-300' : 'text-amber-300'}>{p.text}</div>)}
                  </div>
                  {addPrev.warnings.map(w => <div key={w} className="text-amber-300 text-xs">{w}</div>)}
                  <button onClick={confirmAdd} disabled={addBusy}
                    className="w-full px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-semibold">
                    {addBusy ? 'Adding…' : 'Add pallet'}</button>
                  <div className="text-[11px] text-gray-500">Nothing is written to the shops — a new place is empty; goods get its code when they are moved there.</div>
                </>}
              </div>
            )}
          </div>
        ) : selected ? (
          <div className="mb-6">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-semibold text-white">{selected.id}</h2>
              <button onClick={() => setSel(null)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            <div className="text-gray-400">{selected.kind === 'bay' ? `metal bay · ${selected.system || ''}` : `pallet · ${selected.turn === 'short' ? '2' : '3'} sections per side`}
              {' '}· row {selected.row}{selected.setup ? ` · ${selected.setup}` : ''}</div>
            {selected.note && <div className="text-gray-500 text-xs mt-1">{selected.note}</div>}
            {selected.kind === 'pallet' && canEdit && (
              <div className="mt-3">
                <div className="text-xs text-gray-500 mb-1">Box stacks — the whole pallet:</div>
                <div className="flex flex-wrap gap-1">
                  {BOXES.map(b => <button key={b} onClick={() => setStacks(selected.codes.map(c => c.code), b)}
                    className="px-2.5 py-1 rounded text-white text-xs" style={{ background: BOX_COLOR[b] }}>All {b}</button>)}
                </div>
              </div>
            )}
            <div className="mt-3 space-y-1">
              {selected.codes.map(c => (
                <div key={c.code} className="flex justify-between items-center gap-2">
                  <button onClick={() => navigate(`/warehouse/location/${encodeURIComponent(c.code)}`)} className="text-white font-medium hover:text-blue-300 text-left" title="Open this location: the stack's room, the styles on it and their storage tiers">{codeText(c)} ↗</button>
                  {selected.kind === 'pallet' && canEdit ? (
                    <span className="flex gap-0.5">
                      {BOXES.map(b => <button key={b} onClick={() => setStacks([c.code], c.box === b ? null : b)}
                        className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: c.box === b ? BOX_COLOR[b] : '#374151', color: '#fff' }}>{b}</button>)}
                    </span>
                  ) : c.box && <span className="text-xs text-gray-300">{c.box}</span>}
                  <span className="text-gray-400 text-xs w-14 text-right">{c.pieces} pcs</span>
                </div>
              ))}
            </div>
            <div className="text-gray-400 mt-2">{selected.pieces} pcs{selected.top_brand ? ` · ${selected.top_brand}` : ''}</div>
            {nextInRow(selected) && (
              <button onClick={() => setSel(nextInRow(selected)!.id)} className="mt-2 px-3 py-1.5 rounded bg-gray-700 text-gray-100 text-xs">
                Next {selected.kind === 'pallet' ? 'pallet' : 'bay'} up the row ↑</button>
            )}
            {selected.plan_x !== null && (
              <div className="mt-3 rounded border border-amber-700 bg-amber-900/20 p-2 text-amber-200 text-xs">
                Planned: {Math.round(selected.plan_x)} / {Math.round(selected.plan_y || 0)} cm{selected.plan_by ? ` (by ${selected.plan_by})` : ''} — the dashed outline is where it stands today.
                <button onClick={() => resetPlan(selected.id)} className="block mt-1 text-amber-300 underline">Put it back</button>
              </div>
            )}
            {edit && selected.kind === 'pallet' && (
              <button onClick={() => { const p = posOf(selected); savePlan(selected.id, p.x, p.y, ((p.rot || 0) + 90) % 360); }}
                className="mt-3 ml-2 px-3 py-1.5 rounded bg-gray-700 text-gray-100 text-xs">Turn 90°</button>
            )}
          </div>
        ) : zoneSel ? (
          <div className="mb-6">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-semibold text-white">{zoneSel.name}</h2>
              <button onClick={() => setSelZone(null)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            <div className="text-gray-400 text-xs">{zoneSel.kind === 'station' ? 'station' : 'temporary obstacle — the walk goes around it'}
              {zoneSel.updated_by ? ` · last set by ${zoneSel.updated_by}` : ''}</div>
            {zoneSel.kind === 'obstacle' && canEdit && (
              <>
                <input defaultValue={zoneSel.note || ''} placeholder="What is it (a note)" onBlur={e => saveZone({ id: zoneSel.id, note: e.target.value })}
                  className="mt-2 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-xs" />
                <button onClick={async () => { await post(`floor/zone/${zoneSel.id}/remove`, {}); setSelZone(null); }}
                  className="mt-2 px-3 py-1.5 rounded bg-red-700 text-white text-xs">Remove the obstacle</button>
              </>
            )}
          </div>
        ) : tool.kind === 'special_new' ? (
          <div className="mb-6 rounded-lg border border-amber-700 bg-amber-950/30 p-3">
            <h2 className="text-lg font-semibold text-white">Add a shelf</h2>
            <p className="text-xs text-gray-400 mt-1">A special place that is not on a pallet row — Å 1, Å 2, Å 3 against Inbound station 1 (Fernando 1.11). Fill it in, then click the plan where it stands.</p>
            <label className="block mt-3 text-xs text-gray-300">Code (Å, Ä or Ö + a number, or a name like CAMPAIGN)
              <input value={newSpecial.code} onChange={e => setNewSpecial(s => ({ ...s, code: e.target.value }))} placeholder="Å 4"
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white" /></label>
            <label className="block mt-2 text-xs text-gray-300">Note
              <input value={newSpecial.note} onChange={e => setNewSpecial(s => ({ ...s, note: e.target.value }))} placeholder="what lies here"
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white" /></label>
            <label className="block mt-2 text-xs text-gray-300">Leans on
              <select value={newSpecial.zone_id} onChange={e => setNewSpecial(s => ({ ...s, zone_id: e.target.value === '' ? '' : Number(e.target.value) }))}
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white">
                <option value="">— no station —</option>
                {stations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
            <label className="flex items-center gap-2 mt-3 text-xs text-gray-200">
              <input type="checkbox" checked={newSpecial.pick} onChange={e => setNewSpecial(s => ({ ...s, pick: e.target.checked }))} />
              picked on the way (a stop of the walk, pieces into the cart box) — unticked: taken at the packing table
            </label>
            <label className="flex items-center gap-2 mt-2 text-xs text-gray-200">
              <input type="checkbox" checked={newSpecial.home} onChange={e => setNewSpecial(s => ({ ...s, home: e.target.checked }))} />
              products can live here — their stock location can be set to it (a campaign rack); unticked: single pieces only (Å) or a packing-table place (Ä, Ö)
            </label>
            <div className="mt-3 text-xs text-emerald-300">Now click the plan where the shelf stands.</div>
            <button onClick={() => setTool({ kind: 'none' })} className="mt-2 px-3 py-1.5 rounded bg-gray-700 text-gray-100 text-xs">Cancel</button>
          </div>
        ) : specialSel ? (
          <div className="mb-6">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-semibold text-white">{displayLocation(specialSel.code)}</h2>
              <button onClick={() => setSelSpecial(null)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            <div className="text-gray-400 text-xs">special place · {specialSel.pieces} pcs
              {specialSel.zone_id ? ` · leans on ${stations.find(s => s.id === specialSel.zone_id)?.name || 'a station'}` : ''}
              {specialSel.x_cm === null ? ' · not placed' : ''}</div>
            {canApply ? (
              <>
                <input defaultValue={specialSel.note || ''} placeholder="Note" onBlur={e => saveSpecial({ id: specialSel.id, note: e.target.value })}
                  className="mt-2 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-xs" />
                <label className="flex items-center gap-2 mt-2 text-xs text-gray-200">
                  <input type="checkbox" checked={specialSel.pick} onChange={e => saveSpecial({ id: specialSel.id, pick: e.target.checked })} />
                  picked on the way (a stop of the walk)
                </label>
                <label className="flex items-center gap-2 mt-1 text-xs text-gray-200">
                  <input type="checkbox" checked={!!specialSel.home} onChange={e => saveSpecial({ id: specialSel.id, home: e.target.checked })} />
                  products can live here (their stock location can be set to it)
                </label>
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-300">
                  <span>Size</span>
                  <input type="number" min={10} max={600} step={5} defaultValue={specialSel.w_cm || 130} key={`w${specialSel.id}${specialSel.w_cm}`}
                    onBlur={e => { const v = Number(e.target.value); if (v >= 10 && v !== specialSel.w_cm) saveSpecial({ id: specialSel.id, w: v }); }}
                    className="w-16 bg-gray-900 border border-gray-700 rounded px-1 py-0.5 text-white" />
                  <span>×</span>
                  <input type="number" min={10} max={600} step={5} defaultValue={specialSel.h_cm || 40} key={`h${specialSel.id}${specialSel.h_cm}`}
                    onBlur={e => { const v = Number(e.target.value); if (v >= 10 && v !== specialSel.h_cm) saveSpecial({ id: specialSel.id, h: v }); }}
                    className="w-16 bg-gray-900 border border-gray-700 rounded px-1 py-0.5 text-white" />
                  <span>cm</span>
                  <button onClick={() => saveSpecial({ id: specialSel.id, w: specialSel.h_cm || 40, h: specialSel.w_cm || 130 })} className="px-2 py-0.5 rounded bg-gray-700 text-gray-100">Turn 90°</button>
                </div>
                {specialSel.x_cm !== null && <div className="text-[11px] text-gray-500 mt-1">{edit ? 'Drag it on the plan to adjust its place.' : 'Switch on "Plan moves" to drag it on the plan.'}</div>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => setTool({ kind: 'special_place', id: specialSel.id })} className="px-3 py-1.5 rounded bg-gray-700 text-gray-100 text-xs">{specialSel.x_cm === null ? 'Place on the plan' : 'Move by a click'}</button>
                  <a href={`/tools/wh-labels?codes=${encodeURIComponent(displayLocation(specialSel.code))}`} className="px-3 py-1.5 rounded bg-gray-700 text-gray-100 text-xs">Print its label</a>
                  <button onClick={async () => { if (window.confirm(`Remove ${displayLocation(specialSel.code)} from the plan?`)) { const ok = await saveSpecial({ id: specialSel.id, remove: true }); if (ok) setSelSpecial(null); } }}
                    className="px-3 py-1.5 rounded bg-red-800 text-white text-xs">Remove</button>
                </div>
              </>
            ) : <div className="text-xs text-gray-500 mt-2">{specialSel.note}</div>}
          </div>
        ) : <div className="text-gray-500 mb-6">Click a pallet, bay, station, shelf or obstacle.</div>}

        {mezz && (
          <div className="mb-4">
            <h3 className="text-white font-semibold mb-1">Mezzanine</h3>
            <label className="flex items-center gap-2 text-xs text-gray-300">
              <input type="checkbox" checked={mezz.blocks_walk !== false} disabled={!canEdit}
                onChange={e => saveZone({ id: mezz.id, blocks_walk: e.target.checked })} />
              walks go around it (people do not pass under it)
            </label>
            <div className="text-[11px] text-gray-500 mt-1">Fernando, 29.9: "we can't move straight through pallet rows or the mezzanine". Untick if the way under it is open.</div>
          </div>
        )}
        <h3 className="text-white font-semibold mb-1">Stations</h3>
        <div className="space-y-1 mb-6">
          {stations.map(s => (
            <div key={s.id} className="flex items-center justify-between gap-2">
              <button onClick={() => { setSelZone(s.id); setSel(null); }} className={`text-left ${s.x_cm === null ? 'text-gray-500' : 'text-gray-200'}`}>{s.name}</button>
              {edit && (
                <span className="flex gap-1">
                  <button onClick={() => setTool({ kind: 'station', id: s.id })} className="px-2 py-0.5 rounded bg-gray-700 text-gray-100 text-xs">{s.x_cm === null ? 'Place' : 'Move'}</button>
                  {s.x_cm !== null && <button onClick={() => setTool({ kind: 'entrance', id: s.id })} className="px-2 py-0.5 rounded bg-gray-700 text-gray-100 text-xs">{s.entry_x === null ? 'Entrance' : 'Entrance ✓'}</button>}
                </span>
              )}
              {!edit && <span className="text-xs text-gray-500">{s.x_cm === null ? 'not placed' : s.entry_x === null ? 'no entrance' : 'placed'}</span>}
            </div>
          ))}
          {!edit && <div className="text-xs text-gray-500">Place them in "Plan moves".</div>}
        </div>
        <h3 className="text-white font-semibold mb-1">Special places</h3>
        <div className="space-y-1 mb-6">
          {specials.map(s => (
            <div key={s.id} className="flex items-center justify-between gap-2">
              <button onClick={() => { setSelSpecial(s.id); setSel(null); setSelZone(null); }} className={`text-left ${s.x_cm === null ? 'text-gray-500' : 'text-gray-200'}`}>
                {displayLocation(s.code)}{s.pick ? '' : <span className="text-gray-500"> · packing table</span>}</button>
              {canApply
                ? <button onClick={() => setTool({ kind: 'special_place', id: s.id })} className="px-2 py-0.5 rounded bg-gray-700 text-gray-100 text-xs">{s.x_cm === null ? 'Place' : 'Move'}</button>
                : <span className="text-xs text-gray-500">{s.x_cm === null ? 'not placed' : 'placed'}</span>}
            </div>
          ))}
          {specials.length === 0 && <div className="text-xs text-gray-500">None yet — "+ Add a shelf".</div>}
        </div>

        {(f.fill || []).some(r => r.moved) && canEdit && (
          <div className="mb-4 text-xs text-gray-400">
            Moved fill lines: {(f.fill || []).filter(r => r.moved).map(r => (
              <button key={r.row} onClick={() => saveLine(r.row, null)} className="underline mr-2">{r.row} back to default</button>
            ))}
          </div>
        )}
        {planned.length > 0 && canEdit && (
          <button onClick={() => { if (window.confirm('Drop every planned move?')) resetPlan(null); }}
            className="mb-6 text-xs text-gray-400 underline">Drop all {planned.length} planned moves</button>
        )}

        <h3 className="text-white font-semibold mb-1">Stock on no pallet or bay ({f.orphans.length})</h3>
        <p className="text-xs text-gray-500 mb-2">Codes with stock that the plan has no pallet or bay for — a wrong location on the product, or something the plan is missing.</p>
        <div className="space-y-1">
          {f.orphans.map(o => (
            <div key={o.code} className="flex justify-between gap-2">
              <span className="text-gray-200">{displayLocation(o.code)}</span>
              <span className="text-gray-500 text-xs text-right">{o.pieces} pcs · {o.why}</span>
            </div>
          ))}
        </div>
      </aside>}
    </div>
  );
}
